import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import { Link } from "react-router-dom"
import NavigationBar from "../../components/ui/NavigationBar"
import Icon from "../../components/AppIcon"
import { authHelpers } from "../../firebaseClient"
import MentorshipChat from "../../components/mentorship/MentorshipChat"
import MentorshipAppointments from "../../components/mentorship/MentorshipAppointments"
import {
  subscribeMyMentors,
  subscribeMentorshipSessions,
} from "../../utils/mentorshipHelpers"

const MenteeWorkspace = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mentorships, setMentorships] = useState([])
  const [selected, setSelected] = useState(null) // the active mentorship
  const [subTab, setSubTab] = useState("overview") // "overview" | "messages"

  const [sessions, setSessions] = useState([])

  useEffect(() => {
    const load = async () => {
      const { session } = await authHelpers.getSession()
      if (!session) {
        navigate("/authentication-portal", { replace: true })
        return
      }
      setCurrentUser(session.user)
      setLoading(false)
    }
    load()
  }, [navigate])

  useEffect(() => {
    if (!currentUser) return
    return subscribeMyMentors(currentUser.uid, setMentorships)
  }, [currentUser])

  // Auto-select a mentorship if the dashboard linked here with one in mind,
  // e.g. /mentee-workspace?mentorshipId=abc123 — otherwise default to the
  // first mentorship once the list loads.
  useEffect(() => {
    if (selected || mentorships?.length === 0) return
    const wantedId = searchParams?.get("mentorshipId")
    const match = wantedId ? mentorships?.find((m) => m?.id === wantedId) : null
    setSelected(match || mentorships?.[0])
  }, [mentorships, searchParams, selected])

  useEffect(() => {
    if (!selected) { setSessions([]); return }
    return subscribeMentorshipSessions(selected.orgId, selected.id, setSessions)
  }, [selected])

  useEffect(() => { setSubTab("overview") }, [selected?.id])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading your mentorship...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>My Mentorship - Nesolearn</title></Helmet>
      <NavigationBar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-8">
        <Link
          to="/user-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Dashboard
        </Link>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Mentors</h1>
          <p className="text-muted-foreground mt-1">People mentoring you, across every organization.</p>
        </div>

        {mentorships?.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="HeartHandshake" size={28} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">You haven't been paired with a mentor yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 space-y-2">
              {mentorships?.map((m) => (
                <button
                  key={m?.id}
                  onClick={() => setSelected(m)}
                  className={`w-full text-left bg-card border rounded-lg p-3 transition-smooth ${
                    selected?.id === m?.id ? "border-accent bg-accent/5" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <p className="text-sm font-medium text-gray-900">{m?.mentorName}</p>
                  {m?.goal && <p className="text-xs text-muted-foreground mt-0.5">{m?.goal}</p>}
                </button>
              ))}
            </div>

            <div className="lg:col-span-2 space-y-4">
              {!selected ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground text-sm">
                  Select a mentor to view your session history.
                </div>
              ) : (
                <>
                  <div className="bg-card border border-border rounded-xl p-5 space-y-1">
                    <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                      {selected?.mentorName}
                    </h2>
                    {selected?.goal && (
                      <p className="text-sm text-muted-foreground">Goal: {selected?.goal}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSubTab("overview")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "overview" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setSubTab("messages")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "messages" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Messages
                    </button>
                    <button
                      onClick={() => setSubTab("appointments")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "appointments" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Appointments
                    </button>
                  </div>

                  {subTab === "messages" && (
                    <MentorshipChat
                      orgId={selected.orgId}
                      mentorshipId={selected.id}
                      currentUser={{ uid: currentUser.uid, name: currentUser.displayName || currentUser.email?.split("@")[0] || "Mentee" }}
                      otherUid={selected.mentorUid}
                      otherName={selected.mentorName}
                    />
                  )}

                  {subTab === "appointments" && (
                    <MentorshipAppointments
                      orgId={selected.orgId}
                      mentorshipId={selected.id}
                      currentUser={{ uid: currentUser.uid, name: currentUser.displayName || currentUser.email?.split("@")[0] || "Mentee" }}
                      otherName={selected.mentorName}
                      canPropose={false}
                    />
                  )}

                  {subTab === "overview" && (
                  <div className="bg-card border border-border rounded-xl p-5 space-y-2">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Session History</h2>
                    <p className="text-xs text-muted-foreground -mt-1">Logged by your mentor after each check-in.</p>
                    {sessions?.length === 0 && (
                      <p className="text-sm text-muted-foreground pt-2">No sessions logged yet.</p>
                    )}
                    {sessions?.map((s) => (
                      <div key={s?.id} className="py-2 border-b border-border last:border-0">
                        <p className="text-sm text-foreground">{s?.notes}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {s?.completedAtMs ? new Date(s.completedAtMs).toLocaleDateString() : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default MenteeWorkspace